import { useQuery } from '@tanstack/react-query';

import useUrl from '~/common/hooks/useUrl';

import { typedApiClient } from '../util/api-client';
import useAuthentication from './useAuthentication';

const NAV_FEATURE_FLAGS = {
  IncludeREO: false,
  IncludeAsset: false,
  IncludeBorrower: false,
  IncludeIncome: false,
  IncludeLiability: false,
  IncludeSubjectProperty: false,
  IncomeConvNonArmsLengthGuidelineEnabled: false,
  IsNewSummaryEnabled: false,
  ShowTipMessagesWhenSelfEmploymentIsLessThanTwoYears: false,
  AssetsScreenExpectedAssetsWorkflowEffectiveDate: false,
  IncludeKnowYourLoan: false,
  IsNewLayoutEnabled: false,
  IsInsuranceWindHailLoan: false,
  isAuditHistoryRouteEnabled: false,
  Title: false,
  NoDocumentationProvidedRentalIncome: false,
  EmploymentNoIncomeDocsRevampEnabled: false,
  IsInsuranceHoiEnabled: false,
  IsQuestionLibraryEnabled: false,
  IsREO1003AndPropertyEnabled: false,
  AdditionalReservesInBoltEnabled: false,
  IsInsuranceFloodLoan: false,
  IsReconciliationVariableIncomeFlowEnabled: false,
  isLoanStatusFlipEnabled: false,
  SuppressEINForFHA: false,
  SSIConvDocumentDateGuidelineEnabled: false,
  IncomeSSIAwardLetterDocumentViewer: false,
  IsSSIAwardLetterAuditHistoryRouteEnabled: false,
  MultipleFHAMortgageExceptionConditionEnabled: false,
  Is1120ValidateAuditHistoryRouteEnabled: false,
  IsNewUIIncomeReconciliationPageEnabled: false,
  IsBoltCurrentlyLockedByUserEnabled: false,
  IsActionBarEnabled: false,
  IsFirstTimeHomeBuyer1103PageEnabledBOLTXX: false,
  IsNewCTCEnabled: false,
  IsAusRequestHistoryEnabled: false,
  IsIncomeReconAuditHistoryEnabled: false,
  IsAusActionBarButtonEnabled: false,
  BOLTXXValidateDocumentV2: false,
  Is1120SV2FlowEnabled: false,
  isStickyNavigationEnabled: true,
  IsStatusAndReviewPageEnabled: false,
  IsStatusAndReviewAuditHistoryEnabled: false,
  IsFHASSNMismatchAuditHistoryEnabled: false,
  IsIncomeReconCalculatorPopOverEnabled: false,
  IsDerogatoryCreditEventsAuditHistoryEnabled: false,
  IsFraudAlertAuditHistoryEnabled: false,
  IsIncomeReconRequiredEnabled: false,
  IsRecentRaiseWorkflowEnabled: false,
  IsLpRecentRaiseWorkflowEnabled: false,
  CreateCollectionLiabilityFromDerogatoryEnabled: false,
  IsFloatingMenuEnabled: false,
  IsHotkeysEnabled: false,
  IsConvBalanceSheetRequiredCheckBox: false,
  IsUnacceptableMatchesAppropriateActionQuestionEnabled: false,
  HoiRefinanceWaiverOptOutCheckbox: false,
  IsClassifyDocumentSearchEnabled: false,
  IsFHASelfEmploymentGuidelineScheduleCEnabled: false,
  IsHoiExtendedReplacementActive: false,
  IsHoiInsuranceRatingActive: false,
  IsHoiInvoiceActive: false,
  IsHoiNewPolicyDateSectionActive: false,
  IsHoiNewCardStyleEnabled: false,
  IsReEnergizeTitleCplWireActive: false,
  IsInsuranceWindHailRefinance: false,
  'HoiCondoPilot.PerUnitDeductible': false,
  'HoiCondoPilot.ElectiveInsurance': false,
  'HoiCondoPilot.ActualCashValueTextDiffEnabled': false,
  IsSsiIncomeAutomationEnabled: false,
  IsEnhancedFHADisputedAccountsEnabled: false,
  AssetPilotEligibility: false,
  IsRailNavSummary: false,
  IsScheduleCBoltClearable: false,
  IsScheduleCOverhaulEnabled: false,
  Is1120sBoltClearable: false,
  Is1065NewUIAndConditionsBoltClearable: false,
  Is1120NewUIAndConditionsBoltClearable: false,
  boltRedesignV2: false,
  IsDateTrackingEnabled: false,
  IsMasterNoDocsCheckboxEnabled: false,
  IsMasterInsuranceReplacementCostEnhancement: false,
  IsHO3GuidanceUpdateEnabled: false,
  ArePropertyTaxesIconsEnabled: false,
  IsFHANonReportingPaymentTrackerEnabled: false,
  IsValidate1120SNewUIEnabled: false,
  Is1120SValidateK1NewUIEnabled: false,
  IsSSIAwardLetterValidDocumentsDateCriteriaEnabled: false,
  IsHoiPolicyEndDateValidationActive: false,
  IsHoiMortgageeAddressMatchEnabled: false,
  VAIrrrlObligorAutomationEnabled: false,
  IsVAIrrrlCaseNumberAutohideEnabled: false,
  IsValidate1120SAutomationEnabled: false,
  IsValidate1120ScheduleGAndCalculateLine30ConditionsEnabled: false,
  IsEnhancedAppraisalWorkflowEnabled: false,
};

export type NavFeatureFlags = typeof NAV_FEATURE_FLAGS;

export function useNavFeatureFlagsQuery() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { isAuthorized } = useAuthentication();

  return useQuery({
    queryKey: ['navFeatureFlags', obfuscatedLoanIdentifier],
    staleTime: 60 * 60 * 1000, // 60 minutes
    placeholderData: NAV_FEATURE_FLAGS,
    enabled: !!obfuscatedLoanIdentifier && isAuthorized,
    queryFn: async () => {
      const response = await typedApiClient.v1.loanGetBoltNavFeatureFlags(
        obfuscatedLoanIdentifier,
      );

      return response.data.featureFlags as NavFeatureFlags;
    },
  });
}

export function useNavFeatureFlags() {
  return useNavFeatureFlagsQuery().data ?? NAV_FEATURE_FLAGS;
}
